import React, { useState, useMemo, useCallback } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  Check,
  CheckCheck,
  X as XIcon,
  Edit3,
  MessageSquare,
  ChevronDown,
  Plus,
  Eye,
  Info,
  Loader2,
  RefreshCw,
  Unlock,
  ShieldOff,
} from 'lucide-react';
import type { StageVerificationResult, VerificationFinding } from '../../types/smartStart';
import {
  recordRemediation,
  reVerifyStage,
  overrideVerificationGate,
  VerificationOverrideError,
} from '../../services/smartStartApi';
import toast from 'react-hot-toast';

interface Props {
  sessionId: number;
  stageIndex: number;
  verification: StageVerificationResult;
  onUpdate: () => void;
}

const GROUP_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string; description: string }> = {
  fix_now: { label: 'Fix Now', icon: Edit3, color: 'border-red-200 bg-red-50', description: 'AI has a recommended fix with source evidence' },
  add_missing: { label: 'Add Missing', icon: Plus, color: 'border-amber-200 bg-amber-50', description: 'Gap detected — content missing from source' },
  needs_sme: { label: 'Needs SME', icon: MessageSquare, color: 'border-blue-200 bg-blue-50', description: 'Requires subject-matter expert input' },
  acknowledged: { label: 'Acknowledged', icon: Eye, color: 'border-gray-200 bg-gray-50', description: 'Low severity or informational' },
};

const BAND_COLORS: Record<string, string> = {
  high: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  moderate: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-red-100 text-red-700 border-red-200',
};

const ACTION_LABELS: Record<string, string> = {
  accept: 'Accepted',
  reject: 'Rejected',
  modify: 'Modified',
  acknowledge: 'Acknowledged',
};

const VerificationPanel: React.FC<Props> = ({ sessionId, stageIndex, verification, onUpdate }) => {
  const [expanded, setExpanded] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [notes, setNotes] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [reVerifying, setReVerifying] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [localStatuses, setLocalStatuses] = useState<Record<string, string>>({});
  const [overridePanelOpen, setOverridePanelOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideConfirmChecked, setOverrideConfirmChecked] = useState(false);
  const [overrideBusy, setOverrideBusy] = useState(false);

  if (!verification?.ran) return null;

  const confidence = verification.composite_confidence;
  const findings = verification.remediation_items || [];
  const activeOverride = verification.override || null;
  const isLowConfidence = confidence?.band === 'low';
  const isBlockedByGate = verification.export_eligible === false && !activeOverride;
  const showOverrideAffordance = isLowConfidence && isBlockedByGate;

  const getStatus = (f: VerificationFinding) => localStatuses[f.id] || f.status;

  const pendingCount = findings.filter((f) => getStatus(f) === 'pending').length;
  const fixNowPendingCount = findings.filter(
    (f) => f.actionability === 'fix_now' && f.suggested_fix && getStatus(f) === 'pending'
  ).length;

  const grouped = useMemo(() => {
    const groups: Record<string, VerificationFinding[]> = {
      fix_now: [],
      add_missing: [],
      needs_sme: [],
      acknowledged: [],
    };
    for (const f of findings) {
      const key = f.actionability || 'acknowledged';
      (groups[key] || groups.acknowledged).push(f);
    }
    return groups;
  }, [findings]);

  const handleAction = useCallback(async (findingId: string, action: string, modifiedContent?: string) => {
    let toastId: string | undefined;
    try {
      setBusyId(findingId);
      if (action === 'modify') {
        toastId = toast.loading('Saving modification…');
      }
      await recordRemediation(sessionId, stageIndex, {
        finding_id: findingId,
        action: action as any,
        notes: notes || undefined,
        modified_content: modifiedContent || undefined,
      });
      setLocalStatuses((prev) => ({ ...prev, [findingId]: action === 'accept' ? 'accepted' : action === 'reject' ? 'rejected' : action === 'modify' ? 'modified' : 'acknowledged' }));
      setEditingId(null);
      setEditText('');
      setNotes('');
      if (toastId) toast.dismiss(toastId);
      toast.success(`Finding ${ACTION_LABELS[action] || action}`);
    } catch (err: any) {
      if (toastId) toast.dismiss(toastId);
      toast.error(`Remediation failed: ${err?.response?.data?.error || err.message}`);
    } finally {
      setBusyId(null);
    }
  }, [sessionId, stageIndex, notes]);

  const handleBulkAccept = useCallback(async () => {
    const fixableFindings = findings.filter(
      (f) => f.actionability === 'fix_now' && f.suggested_fix && getStatus(f) === 'pending'
    );
    if (fixableFindings.length === 0) return;

    setBulkBusy(true);
    let succeeded = 0;
    for (const f of fixableFindings) {
      try {
        await recordRemediation(sessionId, stageIndex, {
          finding_id: f.id,
          action: 'accept',
        });
        setLocalStatuses((prev) => ({ ...prev, [f.id]: 'accepted' }));
        succeeded++;
      } catch (err: any) {
        toast.error(`Failed to accept "${f.id}": ${err?.response?.data?.error || err.message}`);
        break;
      }
    }
    setBulkBusy(false);
    if (succeeded > 0) {
      toast.success(`${succeeded} finding${succeeded === 1 ? '' : 's'} accepted`);
    }
  }, [findings, sessionId, stageIndex, localStatuses]);

  const handleReVerify = useCallback(async () => {
    try {
      setReVerifying(true);
      toast.loading('Re-running verification on updated content…', { id: 'reverify' });
      await reVerifyStage(sessionId, stageIndex);
      setLocalStatuses({});
      toast.success('Verification updated', { id: 'reverify' });
      onUpdate();
    } catch (err: any) {
      toast.error(`Re-verification failed: ${err?.response?.data?.error || err.message}`, { id: 'reverify' });
    } finally {
      setReVerifying(false);
    }
  }, [sessionId, stageIndex, onUpdate]);

  const handleConfirmOverride = useCallback(async () => {
    const trimmed = overrideReason.trim();
    if (trimmed.length < 10) {
      toast.error('Please provide a justification of at least 10 characters.');
      return;
    }
    if (!overrideConfirmChecked) {
      toast.error('Please confirm you understand the risk before proceeding.');
      return;
    }
    try {
      setOverrideBusy(true);
      await overrideVerificationGate(sessionId, stageIndex, trimmed);
      toast.success('Override recorded — export gate opened for this stage');
      setOverridePanelOpen(false);
      setOverrideReason('');
      setOverrideConfirmChecked(false);
      onUpdate();
    } catch (err: any) {
      if (err instanceof VerificationOverrideError) {
        toast.error(err.message);
      } else {
        toast.error(`Override failed: ${err?.response?.data?.error || err.message || err}`);
      }
    } finally {
      setOverrideBusy(false);
    }
  }, [overrideReason, overrideConfirmChecked, sessionId, stageIndex, onUpdate]);

  const hasLocalChanges = Object.keys(localStatuses).length > 0;

  const renderScoreBar = (score: number | null, label: string, weight: number) => {
    const pct = score ?? 0;
    const barColor = pct >= 85 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="w-28 text-gray-600 truncate">{label}</span>
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="w-10 text-right font-semibold text-gray-800">
          {score !== null ? `${pct}%` : '—'}
        </span>
        <span className="w-10 text-right text-gray-400">×{weight}</span>
      </div>
    );
  };

  const renderFinding = (finding: VerificationFinding) => {
    const status = getStatus(finding);
    const isDone = status !== 'pending';
    const isThisBusy = busyId === finding.id;

    return (
      <div
        key={finding.id}
        className={`p-3 rounded-lg border transition-all ${isDone ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-gray-500 uppercase font-medium">{finding.type}</span>
            <span className="text-[10px] text-gray-400 uppercase">{finding.severity}</span>
            {finding.issue_type && (
              <span className="text-[10px] text-gray-400">{finding.issue_type.replace(/_/g, ' ')}</span>
            )}
          </div>
          {isDone && (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
              <Check className="w-2.5 h-2.5" />
              {status}
            </span>
          )}
        </div>
        <div className="text-sm text-gray-800 mb-1">{finding.issue}</div>
        {finding.claim && (
          <div className="text-xs text-gray-600 italic mb-1 break-words">Claim: &ldquo;{finding.claim}&rdquo;</div>
        )}
        {finding.evidence && (
          <div className="text-xs text-gray-500 mb-1 bg-gray-50 p-1.5 rounded border border-gray-100 break-words">
            Evidence: {finding.evidence}
          </div>
        )}
        {finding.suggested_fix && (
          <div className="text-xs bg-lavender/5 border border-lavender/20 rounded p-2 mb-2 break-words">
            Suggested fix: {finding.suggested_fix}
          </div>
        )}

        {!isDone && editingId !== finding.id && (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {finding.suggested_fix && (
              <button
                onClick={() => handleAction(finding.id, 'accept')}
                disabled={isThisBusy || bulkBusy}
                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100 disabled:opacity-50"
              >
                {isThisBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Accept fix
              </button>
            )}
            <button
              onClick={() => {
                setEditingId(finding.id);
                setEditText(finding.suggested_fix || finding.claim || '');
                setNotes('');
              }}
              disabled={isThisBusy || bulkBusy}
              className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50"
            >
              <Edit3 className="w-3 h-3" /> Modify
            </button>
            <button
              onClick={() => handleAction(finding.id, 'reject')}
              disabled={isThisBusy || bulkBusy}
              className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-white text-gray-600 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              {isThisBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <XIcon className="w-3 h-3" />}
              Reject
            </button>
            <button
              onClick={() => handleAction(finding.id, 'acknowledge')}
              disabled={isThisBusy || bulkBusy}
              className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              Acknowledge
            </button>
          </div>
        )}

        {editingId === finding.id && (
          <div className="mt-2 space-y-2">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={3}
              className="w-full p-2 text-xs rounded border border-gray-300 focus:border-lavender focus:ring-2 focus:ring-lavender/20 focus:outline-none resize-none"
            />
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for modification (optional)"
              className="w-full p-2 text-xs rounded border border-gray-300 focus:border-lavender focus:outline-none"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleAction(finding.id, 'modify', editText)}
                disabled={isThisBusy || !editText.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-lavender text-white rounded hover:bg-lavender-deep disabled:opacity-50"
              >
                {isThisBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Save modification
              </button>
              <button
                onClick={() => { setEditingId(null); setEditText(''); setNotes(''); }}
                disabled={isThisBusy}
                className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const breakdown = confidence?.breakdown;
  const selfScore = breakdown?.self_verification?.score ?? null;
  const citationScore = breakdown?.citation_verification?.score ?? null;
  const challengerScore = (breakdown as any)?.challenger?.score ?? null;

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-xl mb-4 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          {confidence && confidence.band === 'high' ? (
            <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          ) : confidence && confidence.band === 'moderate' ? (
            <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          )}
          <span className="text-sm font-bold text-gray-900">Verification Results</span>
          {confidence && (
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${
                BAND_COLORS[confidence.band] || 'bg-gray-100 text-gray-600 border-gray-200'
              }`}
            >
              {confidence.overall_score}% — {confidence.band_label}
            </span>
          )}
          {pendingCount > 0 && (
            <span className="text-xs text-red-600 font-medium whitespace-nowrap">
              {pendingCount} finding{pendingCount === 1 ? '' : 's'} pending review
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="text-gray-500">Self-verification</div>
              <div className="font-semibold text-gray-800">
                {verification.self_verification?.ran
                  ? `${verification.self_verification.claims_verified}/${verification.self_verification.claims_extracted} claims`
                  : 'Skipped'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="text-gray-500">Citation match</div>
              <div className="font-semibold text-gray-800">
                {verification.citation?.ran
                  ? `${verification.citation.verified}/${verification.citation.total_claims} verified`
                  : 'No source docs'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="text-gray-500">Challenger</div>
              <div className="font-semibold text-gray-800">
                {verification.challenger?.ran
                  ? `${verification.challenger.issues_found} issue${verification.challenger.issues_found === 1 ? '' : 's'}`
                  : verification.challenger?.blocked
                    ? 'Blocked (API)'
                    : 'Skipped'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="text-gray-500">Gaps found</div>
              <div className="font-semibold text-gray-800">{verification.gaps?.length || 0}</div>
            </div>
          </div>

          {confidence && (
            <div>
              <button
                onClick={() => setShowBreakdown(!showBreakdown)}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800"
              >
                <Info className="w-3 h-3" />
                {showBreakdown ? 'Hide score breakdown' : 'Show score breakdown'}
              </button>
              {showBreakdown && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                  {renderScoreBar(selfScore, 'Self-verification', breakdown?.self_verification?.weight ?? 0.40)}
                  {renderScoreBar(citationScore, 'Citation match', breakdown?.citation_verification?.weight ?? 0.30)}
                  {renderScoreBar(challengerScore, 'Challenger', (breakdown as any)?.challenger?.weight ?? 0.30)}
                  <div className="border-t border-gray-200 pt-2 mt-2 flex items-center justify-between text-xs">
                    <span className="font-semibold text-gray-700">Composite score</span>
                    <span className="font-bold text-gray-900">{confidence.overall_score}%</span>
                  </div>
                  {confidence.explanation && (
                    <div className="text-[11px] text-gray-500 mt-1">{confidence.explanation}</div>
                  )}
                  {verification.integrity && !verification.integrity.valid && (
                    <div className="text-[11px] text-amber-700 mt-1">
                      Integrity issues: {verification.integrity.issues?.join(', ')}
                      {(verification.integrity as any).repaired && ' (auto-repaired)'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeOverride && (
            <div className="p-3 bg-amber-50 border-2 border-amber-300 rounded-lg">
              <div className="flex items-start gap-2">
                <Unlock className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-amber-900 uppercase tracking-wide">
                    Verification gate manually overridden
                  </div>
                  <div className="text-xs text-amber-800 mt-1">
                    Original score{' '}
                    <span className="font-semibold">
                      {activeOverride.original_composite_score != null
                        ? `${activeOverride.original_composite_score}%`
                        : 'unknown'}
                    </span>{' '}
                    was below threshold. Export was unblocked by{' '}
                    <span className="font-semibold">
                      {activeOverride.overridden_by_user_email || `user #${activeOverride.overridden_by_user_id}`}
                    </span>{' '}
                    on {new Date(activeOverride.overridden_at).toLocaleString()}.
                  </div>
                  <div className="text-xs text-amber-900 mt-2 p-2 bg-white/70 border border-amber-200 rounded">
                    <span className="font-semibold">Justification:</span> {activeOverride.reason}
                  </div>
                  <div className="text-[11px] text-amber-700 mt-2">
                    Re-verifying this stage will clear the override and re-evaluate against the gate.
                  </div>
                </div>
              </div>
            </div>
          )}

          {showOverrideAffordance && !overridePanelOpen && (
            <div className="p-4 bg-gradient-to-r from-red-50 to-amber-50 border-2 border-red-300 rounded-xl">
              <div className="flex items-start gap-3">
                <ShieldOff className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-red-900">
                    Export blocked — confidence below threshold
                  </div>
                  <div className="text-xs text-red-800 mt-1">
                    Composite score is{' '}
                    <span className="font-semibold">{confidence?.overall_score ?? 0}%</span>
                    {confidence?.thresholds?.moderate
                      ? ` (minimum ${confidence.thresholds.moderate}% required)`
                      : ''}
                    . Best path is to resolve the findings below and re-verify. If you're certain
                    the artifact is acceptable as-is — for example a demo, a known-good template,
                    or content that the AI under-scored — you can manually unblock the gate. Your
                    justification will be recorded in the audit trail and shown in every export.
                  </div>
                  <button
                    onClick={() => setOverridePanelOpen(true)}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    <Unlock className="w-3.5 h-3.5" />
                    Accept risk &amp; unblock export…
                  </button>
                </div>
              </div>
            </div>
          )}

          {showOverrideAffordance && overridePanelOpen && (
            <div className="p-4 bg-red-50 border-2 border-red-400 rounded-xl space-y-3">
              <div className="flex items-start gap-2">
                <ShieldOff className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-red-900">Confirm risk acceptance</div>
                  <div className="text-xs text-red-800 mt-1">
                    Composite score{' '}
                    <span className="font-semibold">{confidence?.overall_score ?? 0}%</span> is
                    below the{' '}
                    <span className="font-semibold">
                      {confidence?.thresholds?.moderate ?? 60}%
                    </span>{' '}
                    threshold. Accepting the risk will:
                  </div>
                  <ul className="text-xs text-red-800 mt-1 list-disc pl-5 space-y-0.5">
                    <li>Open the export gate for this stage</li>
                    <li>Allow downstream stages and exports to proceed</li>
                    <li>Be recorded in the audit log and export appendix with your reason</li>
                    <li>Be cleared automatically the next time you re-verify this stage</li>
                  </ul>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-red-900 mb-1">
                  Justification <span className="text-red-600">*</span>{' '}
                  <span className="font-normal text-red-700">
                    (min 10 chars, recorded in audit trail)
                  </span>
                </label>
                <textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Demo content for client presentation — accuracy already confirmed offline by SME."
                  maxLength={2000}
                  className="w-full p-2 text-sm rounded-lg border border-red-300 bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none resize-none"
                  disabled={overrideBusy}
                />
                <div className="flex items-center justify-between mt-1">
                  <span
                    className={`text-[11px] ${
                      overrideReason.trim().length < 10 ? 'text-red-600' : 'text-gray-500'
                    }`}
                  >
                    {overrideReason.trim().length < 10
                      ? `${10 - overrideReason.trim().length} more character${
                          10 - overrideReason.trim().length === 1 ? '' : 's'
                        } required`
                      : `${overrideReason.trim().length} / 2000 characters`}
                  </span>
                </div>
              </div>

              <label className="flex items-start gap-2 cursor-pointer p-2 bg-white rounded-lg border border-red-200 hover:bg-red-50 transition">
                <input
                  type="checkbox"
                  checked={overrideConfirmChecked}
                  onChange={(e) => setOverrideConfirmChecked(e.target.checked)}
                  disabled={overrideBusy}
                  className="mt-0.5 w-4 h-4 text-red-600 border-red-300 rounded focus:ring-red-500"
                />
                <span className="text-xs text-red-900">
                  I understand the AI flagged confidence concerns with this output and I take
                  responsibility for proceeding with it.
                </span>
              </label>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  onClick={() => {
                    setOverridePanelOpen(false);
                    setOverrideReason('');
                    setOverrideConfirmChecked(false);
                  }}
                  disabled={overrideBusy}
                  className="px-3 py-2 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmOverride}
                  disabled={
                    overrideBusy || overrideReason.trim().length < 10 || !overrideConfirmChecked
                  }
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {overrideBusy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Unlock className="w-3.5 h-3.5" />
                  )}
                  Override gate &amp; proceed
                </button>
              </div>
            </div>
          )}

          {fixNowPendingCount > 1 && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleBulkAccept}
                disabled={bulkBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
              >
                {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
                Accept all fixes ({fixNowPendingCount})
              </button>
              <span className="text-[11px] text-gray-500">
                Applies the AI-suggested fix for all pending "Fix Now" items
              </span>
            </div>
          )}

          {hasLocalChanges && (
            <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
              <span className="text-xs text-blue-700">
                {Object.keys(localStatuses).length} finding{Object.keys(localStatuses).length === 1 ? '' : 's'} resolved.
              </span>
              <button
                onClick={handleReVerify}
                disabled={reVerifying}
                className="flex items-center gap-1.5 ml-auto px-3 py-1 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {reVerifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Re-verify &amp; update scores
              </button>
            </div>
          )}

          {findings.length > 0 ? (
            <div className="space-y-4">
              {Object.entries(grouped).map(([key, items]) => {
                if (items.length === 0) return null;
                const cfg = GROUP_CONFIG[key];
                const GroupIcon = cfg.icon;
                const groupPending = items.filter((f) => getStatus(f) === 'pending').length;
                return (
                  <div key={key}>
                    <div className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color} mb-2`}>
                      <GroupIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="text-xs font-bold">{cfg.label}</span>
                      <span className="text-xs opacity-70">
                        {groupPending === 0
                          ? `(${items.length} resolved)`
                          : `(${groupPending} of ${items.length} pending)`}
                      </span>
                      <span className="text-[10px] opacity-60 ml-auto hidden sm:inline">{cfg.description}</span>
                    </div>
                    <div className="space-y-2">
                      {items.map(renderFinding)}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg">
              No issues found — all claims passed verification.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VerificationPanel;
